import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Users, 
  Mail, 
  CheckCircle, 
  X, 
  AlertCircle,
  User,
  Lock,
  Eye,
  EyeOff
} from 'lucide-react';
import { invitationsService, authService } from '../services/api';

const AccepterInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [invitation, setInvitation] = useState(null);
  const [utilisateurExistant, setUtilisateurExistant] = useState(false);
  const [compteActif, setCompteActif] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Formulaire pour nouveau compte
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    mot_de_passe: '',
    confirmer_mot_de_passe: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (!token) {
      setError('Token d\'invitation manquant');
      setLoading(false);
      return;
    }
    
    loadInvitation();
  }, [token]);

  const loadInvitation = async () => {
    try {
      setLoading(true);
      const response = await invitationsService.getByToken(token);
      
      if (response.success) {
        setInvitation(response.data.invitation);
        setUtilisateurExistant(response.data.utilisateur_existant);
        setCompteActif(response.data.compte_actif);
      } else {
        setError('Invitation non trouvée');
      }
    } catch (err) {
      console.error('Erreur lors du chargement de l\'invitation:', err);
      if (err.message.includes('expiré')) {
        setError('Cette invitation a expiré');
      } else if (err.message.includes('valide')) {
        setError('Cette invitation n\'est plus valide');
      } else {
        setError('Erreur lors du chargement de l\'invitation');
      }
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.nom.trim()) {
      errors.nom = 'Le nom est requis';
    }
    
    if (!formData.prenom.trim()) {
      errors.prenom = 'Le prénom est requis';
    }
    
    if (!formData.mot_de_passe) {
      errors.mot_de_passe = 'Le mot de passe est requis';
    } else if (formData.mot_de_passe.length < 6) {
      errors.mot_de_passe = 'Le mot de passe doit contenir au moins 6 caractères';
    }
    
    if (formData.mot_de_passe !== formData.confirmer_mot_de_passe) {
      errors.confirmer_mot_de_passe = 'Les mots de passe ne correspondent pas';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Effacer l'erreur du champ modifié
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAccepter = async () => {
    setProcessing(true);
    
    try {
      let donneesUtilisateur = null;
      
      // Si l'utilisateur n'existe pas ou n'est pas actif, valider le formulaire
      if (!utilisateurExistant || !compteActif) {
        if (!validateForm()) {
          setProcessing(false);
          return;
        }
        
        donneesUtilisateur = {
          nom: formData.nom.trim(),
          prenom: formData.prenom.trim(),
          mot_de_passe: formData.mot_de_passe
        };
      }
      
      const response = await invitationsService.accepter(token, donneesUtilisateur);
      
      if (response.success) {
        // Si c'était un nouveau compte, rediriger vers la connexion
        if (!utilisateurExistant || !compteActif) {
          navigate('/login', { 
            state: { 
              message: 'Votre compte a été créé avec succès ! Vous pouvez maintenant vous connecter.',
              email: invitation.email
            }
          });
        } else {
          // Si l'utilisateur existait déjà, rediriger vers l'application
          navigate('/dashboard', { 
            state: { 
              message: `Vous avez rejoint l'équipe "${invitation.equipe.nom}" avec succès !`
            }
          });
        }
      }
    } catch (err) {
      console.error('Erreur lors de l\'acceptation:', err);
      setError('Erreur lors de l\'acceptation de l\'invitation: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleRefuser = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir refuser cette invitation ?')) {
      return;
    }
    
    setProcessing(true);
    
    try {
      await invitationsService.refuser(token);
      navigate('/', { 
        state: { 
          message: 'Invitation refusée'
        }
      });
    } catch (err) {
      console.error('Erreur lors du refus:', err);
      setError('Erreur lors du refus de l\'invitation');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement de l'invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Invitation invalide</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
            >
              Retour à la connexion
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!invitation) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header avec info équipe */}
          <div 
            className="p-6 text-white text-center"
            style={{ backgroundColor: invitation.equipe.couleur }}
          >
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8" />
            </div>
            <h1 className="text-xl font-bold">Invitation à rejoindre</h1>
            <h2 className="text-2xl font-bold">{invitation.equipe.nom}</h2>
            {invitation.equipe.description && (
              <p className="text-sm opacity-90 mt-2">{invitation.equipe.description}</p>
            )}
          </div>

          {/* Contenu principal */}
          <div className="p-6">
            {/* Informations sur l'invitation */}
            <div className="text-center mb-6">
              <div className="flex items-center justify-center space-x-2 text-gray-600 mb-2">
                <Mail className="w-4 h-4" />
                <span className="text-sm">{invitation.email}</span>
              </div>
              <div className="flex items-center justify-center space-x-2 text-gray-600 mb-4">
                <span className="text-sm">
                  Invité par <strong>{invitation.inviteur.prenom} {invitation.inviteur.nom}</strong>
                </span>
              </div>
              <div 
                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                style={{ 
                  backgroundColor: `${invitation.role.couleur}20`,
                  color: invitation.role.couleur 
                }}
              >
                {invitation.role.nom}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Cette invitation expire dans {invitation.temps_restant}
              </p>
            </div>

            {/* Formulaire pour nouveau compte */}
            {(!utilisateurExistant || !compteActif) && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Créer votre compte
                </h3>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Prénom
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          value={formData.prenom}
                          onChange={(e) => handleInputChange('prenom', e.target.value)}
                          className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            formErrors.prenom ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Votre prénom"
                        />
                      </div>
                      {formErrors.prenom && (
                        <p className="text-red-500 text-xs mt-1">{formErrors.prenom}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          value={formData.nom}
                          onChange={(e) => handleInputChange('nom', e.target.value)}
                          className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            formErrors.nom ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Votre nom"
                        />
                      </div>
                      {formErrors.nom && (
                        <p className="text-red-500 text-xs mt-1">{formErrors.nom}</p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mot de passe
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.mot_de_passe}
                        onChange={(e) => handleInputChange('mot_de_passe', e.target.value)}
                        className={`w-full pl-10 pr-10 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          formErrors.mot_de_passe ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Choisissez un mot de passe"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {formErrors.mot_de_passe && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.mot_de_passe}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirmer le mot de passe
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.confirmer_mot_de_passe}
                        onChange={(e) => handleInputChange('confirmer_mot_de_passe', e.target.value)}
                        className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          formErrors.confirmer_mot_de_passe ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Confirmez votre mot de passe"
                      />
                    </div>
                    {formErrors.confirmer_mot_de_passe && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.confirmer_mot_de_passe}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Message pour utilisateur existant */}
            {utilisateurExistant && compteActif && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                  <span className="text-blue-800 font-medium">Compte existant détecté</span>
                </div>
                <p className="text-blue-700 text-sm mt-1">
                  Votre compte est déjà créé. En acceptant cette invitation, vous rejoindrez directement l'équipe.
                </p>
              </div>
            )}

            {/* Boutons d'action */}
            <div className="flex space-x-3">
              <button
                onClick={handleRefuser}
                disabled={processing}
                className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300 disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                <X className="w-4 h-4" />
                <span>Refuser</span>
              </button>
              
              <button
                onClick={handleAccepter}
                disabled={processing}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                {processing && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                <CheckCircle className="w-4 h-4" />
                <span>{processing ? 'Acceptation...' : 'Accepter et rejoindre'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccepterInvitation;
